import { Service } from '@toeverything/infra';
import { isEqual } from 'lodash-es';

import type { EditorSettingService } from '../editor-setting';
import type { I18n } from '../i18n';
import type { Workspace } from '../workspace';
import type { WorkspaceRepositoryService } from '../workspace/services/repo';
import {
  CreationLocationCapture,
  type LocationCaptureRequest,
} from './capture';
import type { LocationProvider } from './provider';
import type { DocCreationLocation } from './types';

export interface CreationLocationRequest extends LocationCaptureRequest {
  generation: number;
}

export class DocCreationLocationService extends Service {
  private readonly capture: CreationLocationCapture;
  private disposed = false;
  private generation = 0;

  constructor(
    provider: LocationProvider,
    private readonly editorSettingService: EditorSettingService,
    i18n: I18n,
    private readonly workspaceRepository: WorkspaceRepositoryService
  ) {
    super();
    this.capture = new CreationLocationCapture(
      provider,
      () => i18n.currentLanguageKey$.value ?? 'en'
    );
    const subscription = editorSettingService.editorSetting.settings$.subscribe(
      settings => {
        if (!settings.captureDocCreationLocation) this.generation++;
        this.capture.setEnabled(settings.captureDocCreationLocation);
      }
    );
    this.disposables.push(() => subscription.unsubscribe());
  }

  get enabled() {
    return (
      !this.disposed &&
      this.editorSettingService.editorSetting.settings$.value
        .captureDocCreationLocation
    );
  }

  beginCapture(): CreationLocationRequest {
    return { ...this.capture.beginCapture(), generation: this.generation };
  }

  async captureForDocs(
    workspace: Workspace,
    docIds: string[],
    pending?: CreationLocationRequest,
    source: 'creation' | 'update' = 'creation'
  ): Promise<void> {
    if (!this.enabled || workspace.openOptions.isSharedMode) return;
    // Importers often release their workspace immediately after creating a
    // document. Keep its engine alive until this metadata has been written.
    let ref: ReturnType<WorkspaceRepositoryService['open']> | undefined;
    try {
      ref = this.workspaceRepository.open(workspace.openOptions);
      const request = pending ?? this.beginCapture();
      const isActive = () =>
        this.enabled && request.generation === this.generation;
      const coordinates = await request.coordinates;
      if (!coordinates || !isActive()) return;
      const location: DocCreationLocation =
        source === 'update' ? { ...coordinates, source } : coordinates;
      await workspace.engine.doc.waitForDocReady(workspace.id);
      if (!isActive()) return;
      const capturedIds: string[] = [];
      for (const id of docIds) {
        const record = workspace.docs.list.doc$(id).value;
        if (
          !record ||
          record.trash$.value ||
          record.getProperties().createdLocation
        )
          continue;
        record.setProperty('createdLocation', location);
        capturedIds.push(id);
      }
      if (!capturedIds.length) return;
      // Flush the device fix locally before waiting for optional enrichment.
      await workspace.engine.doc.waitForUpdated('db$docProperties');
      const enriched = await request.result;
      if (!enriched?.placeName || !isActive()) return;
      let updated = false;
      for (const id of capturedIds) {
        const record = workspace.docs.list.doc$(id).value;
        if (
          !record ||
          record.trash$.value ||
          !isEqual(record.getProperties().createdLocation, location)
        )
          continue;
        record.setProperty(
          'createdLocation',
          source === 'update' ? { ...enriched, source } : enriched
        );
        updated = true;
      }
      if (updated)
        await workspace.engine.doc.waitForUpdated('db$docProperties');
    } catch {
      // Location is optional; never fail creation or recreate removed docs.
    } finally {
      ref?.dispose();
    }
  }

  override dispose() {
    this.disposed = true;
    this.capture.setEnabled(false);
    super.dispose();
  }
}
